import { Fragment, useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { form as formStyles, misc } from 'chaoskit/src/assets/styles/utility'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'
import { Alert, Skeleton } from 'chaoskit/src/components'
import {
  StylesButtonBase,
  StylesButtonDefault,
  StylesButtonPrimary,
  StylesButtonSecondary,
} from 'chaoskit/src/components/Button'
import { StylesInputBase } from 'chaoskit/src/components/Input'
import { StylesCheckboxBase } from 'chaoskit/src/components/Checkbox'
import { StylesRadioBase } from 'chaoskit/src/components/Radio'
import { StylesTextareaBase } from 'chaoskit/src/components/Textarea'
import {
  StylesSelectWrapperBase,
  StylesSelectBase,
} from 'chaoskit/src/components/Select'
import {
  StylesControlWrapper,
  StylesControlWrapperError,
  StylesControlWrapperRequired,
} from 'chaoskit/src/components/ControlWrapper'
import { StylesControlLabel } from 'chaoskit/src/components/ControlLabel'

import LinkedInTrackingPixel from './LinkedInPixel'

import Data from '~components/Data'
import useScript from '~hooks/useScript'
import freeEmailProviders from '~helpers/free-email-providers.json'
import track from '~helpers/track'

const fieldWrapperCallback = (mutationList) => {
  mutationList.forEach((mutation) => {
    const wrapper = mutation.target.closest('.mktoFieldWrap')

    if (mutation.target.getAttribute('aria-invalid') === 'true') {
      wrapper.classList.add('Marketo__Error')
    } else {
      wrapper.classList.remove('Marketo__Error')
    }
  })
}

// Callback function to execute when mutations are observed
const callback = (mutationList) => {
  mutationList.forEach((mutation) => {
    if (mutation.type === 'childList') {
      // Field is visible
      if (mutation.target.querySelector('.mktoFormCol')) {
        mutation.target.classList.remove('Marketo__Placeholder')
      }

      // Field is not visible again
      if (mutation.target.querySelector('.mktoPlaceholder')) {
        mutation.target.classList.add('Marketo__Placeholder')
      }
    }
  })
}

// @NOTE Current limitation of 1 form per page, but https://codepen.io/figureone/pen/ayKadR?editors=1010 may be a workaround if needed in the future
const MarketoForm = ({
  displayOptions = {},
  id,
  onSuccess = () => {},
  fieldValues = {},
  showThankYouMessage = true,
  thankYouMessage,
  disableWorkEmailValidation,
  ...rest
}) => {
  const theme = useTheme()

  const options = {
    submitButtonCentered: true,
    submitButtonType: 'primary',
    columns: false,
    ...displayOptions,
  }

  const observer = useRef()
  const fieldWrapperObserver = useRef()

  const [formLoadError, setFormLoadError] = useState(false)
  const [visible, setVisible] = useState(false)
  const [response, setResponse] = useState()

  // @NOTE Swap back to https://app-sj32.marketo.com when working locally so POST requests work
  const [loaded, error] = useScript(
    'https://chaos.gremlin.com/js/forms2/js/forms2.min.js'
  )

  const destyleMktoForm = (mktoForm) => {
    const formEl = mktoForm.getFormElem()[0]

    // Remove element styles from <form> and children
    const styledEls = Array.from(formEl.querySelectorAll('[style]')).concat(
      formEl
    )
    const style = formEl.querySelectorAll('style')

    style.forEach((el) => {
      el.remove()
    })

    styledEls.forEach((el) => {
      el.removeAttribute('style')
    })

    const marketoBaseStyles = document.querySelector('#mktoForms2BaseStyle')

    if (marketoBaseStyles) {
      marketoBaseStyles.remove()
    }

    const marketoThemeStyles = document.querySelector('#mktoForms2ThemeStyle')

    if (marketoThemeStyles) {
      marketoThemeStyles.remove()
    }

    // Wrap selects in wrapper
    const selectEls = Array.from(formEl.querySelectorAll('select'))

    selectEls.forEach((el) => {
      // create wrapper container
      const wrapper = document.createElement('div')

      wrapper.classList.add('Marketo__Select')

      el.parentNode.insertBefore(wrapper, el)

      wrapper.appendChild(el)
    })

    // All field rows
    const allFieldRows = Array.from(
      formEl.querySelectorAll('.mktoFormRow [id]:not(span):not(label)')
    )

    allFieldRows.forEach((el) => {
      const label = el.closest('.mktoFormRow').querySelector('label')

      if (!label?.innerText) {
        el.closest('.mktoFormRow').classList.add('Marketo__NoLabel')
      }

      if (!['checkbox', 'radio'].includes(el.getAttribute('type'))) {
        if (el.tagName === 'TEXTAREA') {
          el.closest('.mktoFieldWrap').classList.add(
            'Marketo__FieldWrapper',
            'Marketo__FieldWrapper__Textarea'
          )
        } else {
          el.closest('.mktoFieldWrap').classList.add('Marketo__FieldWrapper')
        }
      }
    })

    // Hidden
    const hiddenFields = Array.from(
      formEl.querySelectorAll('.mktoFormRow input[type="hidden"]')
    )

    hiddenFields.forEach((el) => {
      el.closest('.mktoFormRow').classList.add('Marketo__Hidden')
    })

    // Placeholder fields (dynamic)
    const placeholderFields = Array.from(
      formEl.querySelectorAll('.mktoFormRow .mktoPlaceholder')
    )

    placeholderFields.forEach((el) => {
      el.closest('.mktoFormRow').classList.add('Marketo__Placeholder')
    })

    // Create an observer instance linked to the callback function
    if (!observer.current) {
      observer.current = new MutationObserver(callback)
    }

    // Create an observer instance linked to the callback function
    if (!fieldWrapperObserver.current) {
      fieldWrapperObserver.current = new MutationObserver(fieldWrapperCallback)
    }

    const placeholderWrappedFields = Array.from(
      formEl.querySelectorAll('.Marketo__Placeholder')
    )

    placeholderWrappedFields.forEach((el) => {
      // Setup MutationObserver to track placeholder fields rendering with content to remove wrapper class
      observer.current.observe(el, {
        childList: true,
      })
    })

    Array.from(document.querySelectorAll('.mktoField')).forEach((el) => {
      // Setup MutationObserver to track field wrapper changes
      fieldWrapperObserver.current.observe(el, {
        attributes: true,
      })
    })

    // HTML and textarea fields
    const fullWidthFields = Array.from(
      formEl.querySelectorAll(
        '.mktoFormRow textarea, .mktoFormRow .mktoHtmlText'
      )
    )

    fullWidthFields.forEach((el) => {
      el.closest('.mktoFormRow').classList.add('Marketo__FullWidth')
    })

    setVisible(true)
  }

  // Cleanup MutationObserver on unmount
  useEffect(
    () => () => {
      if (observer.current) observer.current.disconnect()

      if (fieldWrapperObserver.current)
        fieldWrapperObserver.current.disconnect()
    },
    []
  )

  useUpdateEffect(() => {
    if ((loaded && !window.MktoForms2) || error) {
      setFormLoadError(true)

      track('Form load error', {
        label: `marketo-${id}`,
      })
    } else if (loaded && window.MktoForms2 && !visible) {
      window.MktoForms2.setOptions({
        formXDPath: '/rs/251-JGH-155/images/marketo-xdframe-relative.html',
      })

      // @NOTE Swap back to https://app-sj32.marketo.com when working locally so POST requests work
      window.MktoForms2.loadForm(
        'https://chaos.gremlin.com',
        '251-JGH-155',
        id,
        (form) => {
          // Set any custom field values
          form.setValues(fieldValues)

          form.onValidate(() => {
            // Get form values
            const values = form.vals()

            // Phone number field check
            // Uses our custom endpoint
            // Normal Marketo validation will kick-in if it's required or not, so an empty string is fine here
            if (values.Phone) {
              // Purposely using blocking request since Marketo callbacks are synchronous only
              // @NOTE Kye's fault for knowing an antequated API
              const request = new XMLHttpRequest()

              request.open('POST', '/.netlify/functions/phone-number', false) // `false` makes the request synchronous
              request.send(
                JSON.stringify({
                  phone: values.Phone,
                  country: values.Country,
                })
              )

              let phoneResponse = { error: false }

              if (request.status === 200) {
                try {
                  phoneResponse = JSON.parse(request.responseText)
                } catch {} // eslint-disable-line no-empty
              }

              if (!phoneResponse.valid) {
                form.submittable(false)

                // Get form field element
                const field = form.getFormElem().find('#Phone')

                // Show error message on field
                form.showErrorMessage('Phone number is invalid', field)
              } else {
                form.submittable(true)
              }
            }

            // Email corporate check
            // Normal Marketo validation will kick-in if it's required or not, so an empty string is fine here
            if (!disableWorkEmailValidation) {
              if (values.Email) {
                const domain = values.Email.substring(
                  values.Email.lastIndexOf('@') + 1
                )

                if (freeEmailProviders.includes(domain)) {
                  form.submittable(false)

                  // Get form field element
                  const field = form.getFormElem().find('#Email')

                  // Show error message on field
                  form.showErrorMessage(
                    'Please provide a valid company email',
                    field
                  )
                }
              } else {
                form.submittable(true)
              }
            }

            form.submittable(form.submittable() !== false)
          })

          form.onSuccess(() => {
            // Get form values
            const values = form.vals()

            onSuccess()

            setResponse('success')

            if (window.analytics) {
              window.analytics.identify(values.Email, {
                email: values.Email,
                firstName: values.FirstName,
                lastName: values.LastName,
                company: values.Company,
                jobTitle: values.Title,
                phone: values.Phone,
              })
            }

            // Disable default redirect from Marketo
            return false
          })
        }
      )

      window.MktoForms2.whenRendered((form) => {
        destyleMktoForm(form)
      })
    }
  }, [loaded, visible])

  return (
    <Fragment>
      {formLoadError && (
        <Alert type="danger">
          Hm, something went wrong. Please try again or reach out to{' '}
          <a
            href={`mailto:hello@gremlin.com?subject=I'm%20having%20trouble%20loading%20a%20form%20on%20gremlin.com&body=Page URL: ${
              typeof window !== 'undefined' ? window.location.href : ''
            }`}
          >
            hello@gremlin.com
          </a>{' '}
          for help.
        </Alert>
      )}
      {response && (
        <Fragment>
          <LinkedInTrackingPixel />
          {showThankYouMessage && (
            <Alert type="primary">
              {thankYouMessage ? (
                <Data>{thankYouMessage}</Data>
              ) : (
                'Success! Your information has been submitted.'
              )}
            </Alert>
          )}
        </Fragment>
      )}
      {!response && !formLoadError && (
        <Fragment>
          {!visible && (
            <div
              css={{
                display: 'grid',
                gridTemplateColumns: '1fr',
                gap: theme.space.base,
              }}
            >
              <Skeleton
                css={{
                  borderRadius: theme.borderRadius.base,
                  height: theme.height.base,
                }}
              />
              <Skeleton
                css={{
                  borderRadius: theme.borderRadius.base,
                  height: theme.height.base,
                }}
              />
              <Skeleton
                css={{
                  borderRadius: theme.borderRadius.base,
                  height: theme.height.base,
                }}
              />
              <Skeleton
                css={{
                  borderRadius: theme.borderRadius.base,
                  height: theme.height.base,
                }}
              />
            </div>
          )}
          <form
            id={`mktoForm_${id}`}
            css={[
              !visible && misc.hide,

              {
                width: '100% !important', // Marketo likes to inline the width for some reason
                display: 'grid',
                gap: theme.space.base,
              },

              options.columns && {
                [theme.mq.medium]: {
                  gridTemplateColumns: 'repeat(16, 1fr)',

                  '.mktoFormRow': {
                    gridColumn: 'span 8',
                  },

                  '.Marketo__FullWidth': {
                    gridColumn: 'span 16',
                  },
                },
              },

              {
                // Hide hidden fields
                // @NOTE Wrapper class added above
                '.Marketo__Hidden, .Marketo__Placeholder': misc.hide,

                // Extra space for submit button
                '.mktoButtonRow': [
                  {
                    marginTop: theme.space.base,
                  },

                  options.columns && {
                    [theme.mq.medium]: {
                      gridColumn: 'span 16',
                    },
                  },

                  options.submitButtonCentered && {
                    textAlign: 'center',
                  },
                ],

                '.mktoButton': [
                  StylesButtonBase(theme, { noContrast: true }),

                  options.submitButtonType === 'default' && [
                    StylesButtonDefault(theme, { noContrast: true }),
                  ],
                  options.submitButtonType === 'primary' && [
                    StylesButtonPrimary(theme, { noContrast: true }),
                  ],
                  options.submitButtonType === 'secondary' && [
                    StylesButtonSecondary(theme, { noContrast: true }),
                  ],
                ],

                // Wrap control around fields
                '.Marketo__FieldWrapper': [
                  StylesControlWrapper(theme),

                  {
                    '&.mktoRequiredField': [
                      StylesControlWrapperRequired(theme, {
                        iconAlignment: 'center',
                      }),

                      {
                        '&.Marketo__FieldWrapper__Textarea': [
                          StylesControlWrapperRequired(theme, {
                            iconAlignment: 'top',
                          }),
                        ],
                      },

                      {
                        '&.Marketo__Error': [StylesControlWrapperError(theme)],
                      },
                    ],
                  },

                  {
                    '.mktoLabel': [StylesControlLabel(theme)],
                  },
                ],

                // We can then hide the asterisk
                '.mktoAsterix': {
                  display: 'none',
                },

                // Hide labels visibly (for now) for required fields that don't have a label
                '.Marketo__NoLabel .mktoLabel': {
                  display: 'none',
                },

                // Error message
                '.mktoError': {
                  color: theme.color.danger.base,
                  ...theme.text.xsmall,
                  position: 'absolute',
                  right: `${
                    formStyles.variables(theme).controlOffset
                  }px !important`,
                  bottom: 'auto !important',
                  top: `calc(100% + 1px)`, // This is definitely not ideal, but we're locked-into what the Marketo DOM provides out of the box

                  '.u-contrast &': {
                    color: theme.contrast.base,
                  },
                },

                //
                // Form fields
                //

                // Text input
                'input.mktoField:not([type="checkbox"]):not([type="radio"])': [
                  StylesInputBase(theme),
                ],

                // Textarea
                'textarea.mktoField': [StylesTextareaBase(theme)],

                // Checkbox + Radio
                '.mktoLogicalField': {
                  display: 'grid',
                  alignItems: 'start',
                  gap: theme.space.small,
                  gridTemplateColumns: 'min-content 1fr',
                },

                // Checkbox
                'input[type="checkbox"]': [
                  StylesCheckboxBase(theme, { noContrast: true }),
                ],

                // Radio
                'input[type="radio"]': [
                  StylesRadioBase(theme, { noContrast: true }),
                ],

                // Select (not required)
                '.Marketo__Select': [
                  StylesSelectWrapperBase(theme),
                  {
                    select: [StylesSelectBase(theme)],
                  },
                ],

                // Select (required)
                '.mktoRequiredField .Marketo__Select': [
                  StylesSelectWrapperBase(theme, {
                    required: true,
                  }),
                ],

                // HTML fields
                '.mktoHtmlText': {
                  ...theme.text.small,
                },
              },
            ]}
            {...rest}
          />
        </Fragment>
      )}
    </Fragment>
  )
}

MarketoForm.propTypes = {
  displayOptions: PropTypes.shape({
    /* Centers submit button on >= medium screens */
    submitButtonCentered: PropTypes.bool,
    /* Applies style variations to the submit button */
    submitButtonType: PropTypes.oneOf(['default', 'primary', 'secondary']),
    /* 2-column layout on >= medium screens */
    columns: PropTypes.bool,
  }),
  id: PropTypes.oneOfType([
    PropTypes.string.isRequired,
    PropTypes.number.isRequired,
  ]),
  onSuccess: PropTypes.func,
  /** Take over success render message based on `onSuccess` */
  showThankYouMessage: PropTypes.bool,
  /** Passes through values that will be statically set on the form. Useful for hidden values like `programId` */
  fieldValues: PropTypes.object,
  thankYouMessage: PropTypes.any,
  disableWorkEmailValidation: PropTypes.bool,
}

export default MarketoForm
